import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChatThread from "@/components/messaging/ChatThread";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MessageSquare, Plus, Users, Search, Hash, Folder } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  const s = Math.floor((now - d) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};
const initials = (p) => ((p?.name || p?.email || "?").slice(0, 2)).toUpperCase();

function NewConversationDialog({ open, onOpenChange, people, me, onOpened }) {
  const [mode, setMode] = useState("dm");
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setMode("dm"); setQ(""); setName(""); setPicked(new Set()); } }, [open]);

  const list = people.filter((p) => p.user_id !== me &&
    (!q || `${p.name || ""} ${p.email || ""}`.toLowerCase().includes(q.toLowerCase())));

  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const startDm = async (uid) => {
    setBusy(true);
    try { const { data } = await api.post("/messaging/dm", { user_id: uid }); onOpened(data.channel_id); }
    finally { setBusy(false); }
  };
  const createChannel = async () => {
    if (!name.trim() || picked.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/messaging/channels", { name: name.trim(), member_ids: [...picked] });
      onOpened(data.channel_id);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-conversation-dialog">
        <DialogHeader>
          <DialogTitle>New conversation</DialogTitle>
          <DialogDescription>Start a direct message or create a group channel.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          <button data-testid="new-conv-mode-dm" onClick={() => setMode("dm")}
            className={`flex-1 text-sm py-1.5 rounded-md transition ${mode === "dm" ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-500"}`}>Direct message</button>
          <button data-testid="new-conv-mode-channel" onClick={() => setMode("channel")}
            className={`flex-1 text-sm py-1.5 rounded-md transition ${mode === "channel" ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-500"}`}>Group channel</button>
        </div>
        {mode === "channel" && (
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Channel name" data-testid="new-channel-name" />
        )}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teammates" className="pl-8" data-testid="new-conv-search" />
        </div>
        <div className="max-h-64 overflow-auto -mx-1">
          {list.length === 0 && <div className="text-xs text-slate-400 px-3 py-3">No teammates found.</div>}
          {list.map((p) => (
            mode === "dm" ? (
              <button key={p.user_id} disabled={busy} onClick={() => startDm(p.user_id)} data-testid={`new-conv-person-${p.user_id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 rounded-md text-left disabled:opacity-60">
                <span className="w-8 h-8 rounded-full bg-[#003E87]/10 text-[#003E87] grid place-items-center text-xs font-semibold shrink-0">{initials(p)}</span>
                <span className="min-w-0"><span className="text-sm text-slate-900 block truncate">{p.name || p.email}</span><span className="text-xs text-slate-400 block truncate">{p.email}</span></span>
              </button>
            ) : (
              <label key={p.user_id} data-testid={`new-conv-member-${p.user_id}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 rounded-md cursor-pointer">
                <Checkbox checked={picked.has(p.user_id)} onCheckedChange={() => toggle(p.user_id)} />
                <span className="w-8 h-8 rounded-full bg-[#003E87]/10 text-[#003E87] grid place-items-center text-xs font-semibold shrink-0">{initials(p)}</span>
                <span className="min-w-0"><span className="text-sm text-slate-900 block truncate">{p.name || p.email}</span></span>
              </label>
            )
          ))}
        </div>
        {mode === "channel" && (
          <DialogFooter>
            <Button onClick={createChannel} disabled={busy || !name.trim() || picked.size === 0}
              className="bg-[#003E87] hover:bg-[#00306a]" data-testid="new-channel-create">
              Create channel {picked.size > 0 && `(${picked.size})`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const me = user?.user_id;
  const [sp, setSp] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [people, setPeople] = useState([]);
  const [active, setActive] = useState(sp.get("c") || null);
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const loadChannels = useCallback(async () => {
    try { const { data } = await api.get("/messaging/channels"); setChannels(data.channels || []); }
    catch { /* ignore */ }
  }, []);

  useEffect(() => { loadChannels(); const t = setInterval(loadChannels, 8000); return () => clearInterval(t); }, [loadChannels]);
  useEffect(() => { api.get("/messaging/people").then((r) => setPeople(r.data || [])).catch(() => {}); }, []);

  // Deep-link (?c=) selects a conversation; default to the freshest one on first load.
  useEffect(() => {
    const c = sp.get("c");
    if (c) { setActive(c); return; }
    if (!active && channels.length) setActive(channels[0].channel_id);
  }, [sp, channels]); // eslint-disable-line react-hooks/exhaustive-deps

  const openChannel = (cid) => {
    setActive(cid);
    setSp((prev) => { const n = new URLSearchParams(prev); n.set("c", cid); return n; }, { replace: true });
    setChannels((cs) => cs.map((c) => (c.channel_id === cid ? { ...c, unread: 0 } : c)));
  };

  const onOpened = async (cid) => { setNewOpen(false); await loadChannels(); openChannel(cid); };

  const filtered = useMemo(() => channels.filter((c) =>
    !q || (c.title || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.last_message || "").toLowerCase().includes(q.toLowerCase())), [channels, q]);

  const activeCh = channels.find((c) => c.channel_id === active);

  return (
    <div className="h-full flex flex-col" data-testid="messages-page">
      <div className="shrink-0 h-14 border-b border-slate-200 bg-white flex items-center gap-2 px-5">
        <MessageSquare className="w-5 h-5 text-[#003E87]" />
        <h1 className="text-lg font-semibold text-slate-900">Messages</h1>
        <span className="text-xs text-slate-400 hidden sm:inline">Talk directly with your team</span>
      </div>
      <div className="flex-1 flex min-h-0">
        {/* Conversation list */}
        <aside className="w-72 shrink-0 border-r border-slate-200 bg-white flex flex-col" data-testid="conversation-list">
          <div className="p-2.5 border-b border-slate-100 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="pl-8 h-9" data-testid="conversation-search" />
            </div>
            <Button size="icon" onClick={() => setNewOpen(true)} className="h-9 w-9 bg-[#003E87] hover:bg-[#00306a] shrink-0" data-testid="new-conversation-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-auto">
            {filtered.length === 0 && (
              <div className="text-xs text-slate-400 px-4 py-6 text-center">No conversations yet.<br />Tap + to start one.</div>
            )}
            {filtered.map((c) => (
              <button key={c.channel_id} onClick={() => openChannel(c.channel_id)} data-testid={`conversation-${c.channel_id}`}
                className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left border-b border-slate-50 transition ${active === c.channel_id ? "bg-[#003E87]/5" : "hover:bg-slate-50"}`}>
                <span className={`w-9 h-9 rounded-full grid place-items-center text-xs font-semibold shrink-0 mt-0.5 ${c.project ? "bg-emerald-100 text-emerald-700" : c.kind === "dm" ? "bg-[#003E87]/10 text-[#003E87]" : "bg-amber-100 text-amber-700"}`}>
                  {c.project ? <Folder className="w-4 h-4" /> : c.kind === "dm" ? initials(c.members?.find((m) => m.user_id !== me) || {}) : <Hash className="w-4 h-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-1">
                    <span className={`text-sm truncate ${c.unread ? "font-semibold text-slate-900" : "text-slate-700"}`}>{c.title}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(c.last_message_at)}</span>
                  </span>
                  <span className="flex items-center justify-between gap-1">
                    <span className={`text-xs truncate ${c.unread ? "text-slate-600" : "text-slate-400"}`}>
                      {c.last_message ? `${c.last_author_name ? c.last_author_name.split(" ")[0] + ": " : ""}${c.last_message}` : "No messages yet"}
                    </span>
                    {c.unread > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F4C400] text-black text-[10px] font-bold grid place-items-center shrink-0" data-testid={`conversation-unread-${c.channel_id}`}>{c.unread > 9 ? "9+" : c.unread}</span>
                    )}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </aside>
        {/* Thread */}
        <section className="flex-1 flex flex-col min-w-0 bg-slate-50">
          {activeCh ? (
            <>
              <div className="shrink-0 h-12 border-b border-slate-200 bg-white flex items-center gap-2 px-4">
                {activeCh.project ? <Folder className="w-4 h-4 text-emerald-600" /> : activeCh.kind === "dm" ? <MessageSquare className="w-4 h-4 text-[#003E87]" /> : <Hash className="w-4 h-4 text-amber-600" />}
                <span className="text-sm font-semibold text-slate-900 truncate">{activeCh.title}</span>
                {activeCh.kind === "channel" && !activeCh.project && (
                  <span className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{(activeCh.members || []).length}</span>
                )}
                {activeCh.project && <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Project</span>}
              </div>
              <div className="flex-1 min-h-0">
                <ChatThread key={active} channelId={active} onActivity={loadChannels} />
              </div>
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-sm text-slate-400" data-testid="no-conversation-selected">
              <div className="text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                Select a conversation or start a new one.
              </div>
            </div>
          )}
        </section>
      </div>
      <NewConversationDialog open={newOpen} onOpenChange={setNewOpen} people={people} me={me} onOpened={onOpened} />
    </div>
  );
}
